import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../../ts/Glob';
import { config } from '../../ts/config';
import { ngettext } from '../../../../_common/platform/ts/ngettext';
import DirsWorkspacesFlat from '../DirsWorkspacesFlat';
import { MenuModes } from '../menus/Common';
import { IMarkedItems } from '../../ts/Interfaces';
import { ISelectedItem } from '../DirsTreeFlat';
import { IDirItem } from '../../ts/stores/DirsStore';
import { IDirContentItem } from '../../ts/stores/WorkspacesDirsStore';

import {
    createNodeID,
    DESCRIPTION_TYPE,
    IDiscriptionTreeNode,
    IExportStore,
    parseNodeID
} from '../../../../_common/platform/ts/exportImportDispatcher';

export interface IWorkspacesExportData {
    selectedDir?: number;
    dirs: string[];
    workspaces: string[];
}

interface IStateValue {
    selectedDir: number;
    markedDirs: IMarkedItems;
    markedWorkspaces: IMarkedItems;
}

function createStateValue(value: IWorkspacesExportData, selectedItems: ISelectedItem[]) {
    let result: IStateValue;

    if (value != null) {
        const markedDirs: IMarkedItems = {};
        const markedWorkspaces: IMarkedItems = {};

        if (selectedItems != null) {
            selectedItems.forEach((item) => {
                if (item.type === 0) {
                    markedDirs[item.id] = item.title;
                } else {
                    markedWorkspaces[item.id] = item.title;
                }
            });
        }

        result = {
            selectedDir: value.selectedDir ?? config.WIDGETS_ROOT_DIR_ID,
            markedDirs: markedDirs,
            markedWorkspaces: markedWorkspaces
        };
    } else {
        result = {
            selectedDir: config.WIDGETS_ROOT_DIR_ID,
            markedDirs: {},
            markedWorkspaces: {}
        };
    }

    return result;
}

function getResultValue(value: IStateValue) {
    const newValue: IWorkspacesExportData = {
        dirs: _.keys(value.markedDirs),
        workspaces: _.keys(value.markedWorkspaces)
    };

    if (value.selectedDir != null) {
        newValue.selectedDir = value.selectedDir;
    }

    const selectedItems: ISelectedItem[] = [];

    _.each(value.markedDirs, (title, id) => {
        selectedItems.push({ type: 0, id: id, title: title });
    });
    _.each(value.markedWorkspaces, (title, id) => {
        selectedItems.push({ type: 1, id: id, title: title });
    });

    return { newValue, selectedItems };
}

function getSelectedMessage(value: IStateValue) {
    const markedDirs = Object.values(value.markedDirs);
    const dirsNumber = markedDirs.length;
    const markedWorkspaces = Object.values(value.markedWorkspaces);
    const workspacesNumber = markedWorkspaces.length;

    if (dirsNumber === 0 && workspacesNumber === 0) {
        return [<span key={'ask-ws-span'}>{'Select folders and workspaces to export.'}</span>];
    }

    const result = _.compact([
        dirsNumber > 0 ? (
            <div key={'dirs-div-ws'} className="selected-message-block">
                <strong>{markedDirs.join(', ')}</strong>
                <span>{ngettext('folder', 'folders', dirsNumber)}</span>
            </div>
        ) : null,

        workspacesNumber > 0 ? (
            <div key={'item-div-ws'} className="selected-message-block">
                <strong>{markedWorkspaces.join(', ')}</strong>
                <span>{ngettext('workspace', 'workspaces', workspacesNumber)}</span>
            </div>
        ) : null
    ]);

    if (result.length === 2) {
        result.splice(1, 0, <span key={'and-span-ws'}>{'and'}</span>);
    }

    result.unshift(<span key={'you-span-ws'}>{'You have selected'}</span>);
    return result;
}

export default function WorkspacesExport(props: {
    value: IWorkspacesExportData;
    selectedItems: ISelectedItem[];
    glob: IGlob;

    onChange(value: IWorkspacesExportData, selectedItems: any[]): void;
}) {
    const [value, setValue] = React.useState<IStateValue>(createStateValue(props.value, props.selectedItems));

    const processOnChange = React.useCallback(
        (value: IStateValue) => {
            if (props.onChange != null) {
                const { newValue, selectedItems } = getResultValue(value);

                props.onChange(newValue, selectedItems);
            }

            setValue(value);
        },
        [props.onChange]
    );

    const handleOnDirSelect = (id: number) => {
        processOnChange({
            ...value,
            selectedDir: id
        });
    };

    const handleOnDirsMark = (marked: IMarkedItems) => {
        processOnChange({
            ...value,
            markedDirs: marked
        });
    };

    const handleOnWorkspacesMark = (marked: IMarkedItems) => {
        processOnChange({
            ...value,
            markedWorkspaces: marked
        });
    };

    const handleOnMark = (markedDirs: IMarkedItems, markedWorkspaces: IMarkedItems) => {
        processOnChange({
            ...value,
            markedDirs: markedDirs,
            markedWorkspaces: markedWorkspaces
        });
    };

    return (
        <div className="workspaces-export">
            <div className="selected-message">{getSelectedMessage(value)}</div>

            <DirsWorkspacesFlat
                instanceID={'workspaces-export'}
                selectedDirId={value.selectedDir}
                markMode={true}
                markedDirs={value.markedDirs}
                markedWorkspaces={value.markedWorkspaces}
                hideIcons={false}
                menuMode={MenuModes.sideBar}
                glob={props.glob}
                onDirSelect={handleOnDirSelect}
                onDirsMark={handleOnDirsMark}
                onWorkspacesMark={handleOnWorkspacesMark}
                onMark={handleOnMark}
            />
        </div>
    );
}

export const WORKSPACES_EXPORT_ID = 'workspaces';

const DIR_TYPE = 'dir';
const WORKSPACE_TYPE = 'ws';

export class WorkspacesExportStore implements IExportStore {
    private _glob: IGlob;
    // Contains all dirs as plain list grouped by parent ID.
    private _plainBranchCollection: Map<string, IDiscriptionTreeNode[]>;

    constructor(glob: IGlob) {
        this._glob = glob;
        this._plainBranchCollection = undefined;
    }

    public get descriptionID(): string {
        return WORKSPACES_EXPORT_ID;
    }

    public createExportData(values: string[]) {
        const dirs: string[] = [];
        const workspaces: string[] = [];

        values.forEach((value) => {
            const { type, id } = parseNodeID(value);

            if (type === DIR_TYPE) {
                dirs.push(id);
            } else if (type === WORKSPACE_TYPE) {
                workspaces.push(id);
            }
        });

        const result: IWorkspacesExportData = { dirs: dirs, workspaces: workspaces };

        return result;
    }

    private addDirBranch(dirItems: IDirItem[], parentID: string) {
        dirItems?.forEach((dirItem) => {
            const id = dirItem.dirid.toString();
            const nodeID = createNodeID(WORKSPACES_EXPORT_ID, DIR_TYPE, id);

            let branch = this._plainBranchCollection.get(parentID);

            if (branch == null) {
                branch = [];
                this._plainBranchCollection.set(parentID, branch);
            }

            branch.push({
                key: nodeID,
                descriptionID: WORKSPACES_EXPORT_ID,
                id: nodeID,
                title: dirItem.name ?? '',
                parentId: parentID,
                subNodesAreNotLoaded: true,
                icon: <span className="folder-icon glyphicon glyphicon-folder-close" />
            });

            if (branch.length > 1) {
                branch.sort((node1, node2) => node1.title.localeCompare(node2.title));
            }

            if (!this._plainBranchCollection.has(nodeID)) {
                this._plainBranchCollection.set(nodeID, []);
            }

            if (dirItem.subdirlist != null) {
                this.addDirBranch(dirItem.subdirlist, nodeID);
            }
        });
    }

    private async loadDirs() {
        return new Promise<Map<string, IDiscriptionTreeNode[]>>((resolve, reject) => {
            if (this._plainBranchCollection != null) {
                resolve(this._plainBranchCollection);
            } else {
                this._glob.DirsStore.getSubDirTree(config.WIDGETS_ROOT_DIR_ID)
                    .done((responce) => {
                        const dirItems = responce.result as IDirItem[];
                        const nodeID = createNodeID(WORKSPACES_EXPORT_ID, DESCRIPTION_TYPE, WORKSPACES_EXPORT_ID);

                        this._plainBranchCollection = new Map<string, IDiscriptionTreeNode[]>();
                        this.addDirBranch(dirItems, nodeID);
                        resolve(this._plainBranchCollection);
                    })
                    .fail(() => {
                        reject();
                    });
            }
        });
    }

    public async getBranchContent(parentID: string) {
        return new Promise<IDiscriptionTreeNode[]>((resolve, reject) => {
            this.loadDirs()
                .then((dirs) => {
                    const node = parseNodeID(parentID);
                    const dirID = parseInt(node.id);

                    this._glob.WorkspacesDirsStore.loadDirContent(isNaN(dirID) ? 1 : dirID)
                        .done((response) => {
                            const treeNodes = dirs.get(parentID);
                            const workspaces = response.result as IDirContentItem[];

                            if (workspaces?.length > 0) {
                                workspaces.forEach((workspace) => {
                                    const { wsid } = workspace;
                                    const nodeID = createNodeID(WORKSPACES_EXPORT_ID, WORKSPACE_TYPE, wsid.toString());

                                    treeNodes.push({
                                        key: nodeID,
                                        id: nodeID,
                                        title: workspace.name,
                                        descriptionID: WORKSPACES_EXPORT_ID,
                                        parentId: parentID,
                                        icon: <span className="glyphicon glyphicon-th-large" />
                                    });
                                });
                            }

                            resolve(treeNodes);
                        })
                        .fail(() => {
                            reject();
                        });
                })
                .catch(() => {
                    reject();
                });
        });
    }
}
